import ExcelJS from "exceljs";

export default class ComprasExcelExporter {
  static async exportPorProveedor(data, title = "Compras por Proveedor") {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Proveedores");

    // Definir las columnas para que ExcelJS cree la fila de encabezados (inicialmente en la fila 1)
    worksheet.columns = [
      { header: "Documento", key: "Documento" },
      { header: "Fecha", key: "Fecha" },
      { header: "Descripción", key: "Descripcion" },
      { header: "Almacén", key: "Almacen" },
      { header: "Proveedor", key: "Proveedor" },
      { header: "Total", key: "Total", style: { numFmt: "#,##0.00" } },
    ];

    // Insertar dos filas al inicio para desplazar el encabezado:
    // La fila 1 será para el título y la fila 2 quedará vacía.
    worksheet.insertRow(1, [title]);
    worksheet.insertRow(2, []);

    // Fusionar las celdas de la fila 1 y darle formato al título.
    worksheet.mergeCells("A1:F1");
    const titleCell = worksheet.getCell("A1");
    titleCell.font = { size: 16, bold: true };
    titleCell.alignment = { vertical: "middle", horizontal: "center" };
    titleCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFADD8E6" }, // Azul claro
    };

    // La fila de encabezados se ha desplazado a la fila 3, así que se le aplica formato.
    const headerRow = worksheet.getRow(3);
    headerRow.font = { bold: true, size: 12 };
    headerRow.alignment = { horizontal: "center" };
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD3D3D3" }, // Gris claro
      };
      cell.border = {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
      };
    });

    // Colores para alternar filas de datos por compra (campo "Documento")
    const greenFill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFB0E0E6" },
    }; // Azul pastel
    const yellowFill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFEEEEEE" },
    }; // Gris muy claro

    let currentDocumento = null;
    let useGreen = true;

    // A partir de la fila 4 se agregarán los datos.
    data.forEach((item) => {
      // Si es la primera compra o el campo "Documento" cambia, alterna el color.
      if (currentDocumento === null || item.Documento !== currentDocumento) {
        currentDocumento = item.Documento;
        useGreen = !useGreen;
      }

      const row = worksheet.addRow({
        Documento: item.Documento,
        Fecha: item.Fecha,
        Descripcion: item.Descripcion,
        Almacen: item.Almacen,
        Proveedor: item.Proveedor,
        Total: item.Total || 0,
      });

      // Aplicar el color de fondo según la compra.
      row.eachCell({ includeEmpty: true }, (cell) => {
        cell.fill = useGreen ? greenFill : yellowFill;
      });
    });

    // Autoajuste de columnas según el contenido.
    worksheet.columns.forEach((column) => {
      let maxLength = column.header ? column.header.toString().length : 10;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const cellLength = cell.value ? cell.value.toString().length : 10;
        if (cellLength > maxLength) maxLength = cellLength;
      });
      column.width = maxLength + 5;
    });

    return workbook.xlsx.writeBuffer();
  }

  static async exportPorItems(data, title = "Compras por Ítems") {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Items de Compra");

    // Definir las columnas para que se genere la fila de encabezados (inicialmente en la fila 1)
    worksheet.columns = [
      { header: "Documento", key: "Documento" },
      { header: "Fecha", key: "Fecha" },
      { header: "Descripción", key: "Descripcion" },
      { header: "Almacén", key: "Almacen" },
      { header: "Proveedor", key: "Proveedor" },
      { header: "Producto", key: "Producto" },
      { header: "Presentación", key: "Presentacion" },
      {
        header: "Precio Unitario",
        key: "PrecioUnitario",
        style: { numFmt: "#,##0.00" },
      },
      { header: "Cantidad", key: "Cantidad" },
      {
        header: "Precio Total",
        key: "PrecioTotal",
        style: { numFmt: "#,##0.00" },
      },
    ];

    // Insertar dos filas al inicio para desplazar el encabezado.
    worksheet.insertRow(1, [title]);
    worksheet.insertRow(2, []);

    // Fusionar las celdas de la fila 1 y darle formato al título.
    worksheet.mergeCells("A1:J1");
    const titleCell = worksheet.getCell("A1");
    titleCell.font = { size: 16, bold: true };
    titleCell.alignment = { vertical: "middle", horizontal: "center" };
    titleCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFADD8E6" }, // Azul claro
    };

    // La fila de encabezados se encuentra en la fila 3 y se le aplica formato.
    const headerRow = worksheet.getRow(3);
    headerRow.font = { bold: true, size: 12 };
    headerRow.alignment = { horizontal: "center" };
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD3D3D3" }, // Gris claro
      };
      cell.border = {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
      };
    });

    // Colores para alternar filas de datos por compra (campo "Documento")
    const blueFill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFB0E0E6" },
    }; // Azul pastel
    const greyFill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFEEEEEE" },
    }; // Gris muy claro

    let currentDocumento = null;
    let useBlue = true;

    // Agregar filas de datos (a partir de la fila 4)
    data.forEach((item) => {
      if (currentDocumento === null || item.Documento !== currentDocumento) {
        currentDocumento = item.Documento;
        useBlue = !useBlue;
      }

      const row = worksheet.addRow({
        Documento: item.Documento,
        Fecha: item.Fecha,
        Descripcion: item.Descripcion,
        Almacen: item.Almacen,
        Proveedor: item.Proveedor,
        Producto: item.Producto,
        Presentacion: item.Presentacion,
        PrecioUnitario: item["Precio Unitario"] || 0,
        Cantidad: Number(item.Cantidad),
        PrecioTotal: item["Precio Total"] || 0,
      });

      row.eachCell({ includeEmpty: true }, (cell) => {
        cell.fill = useBlue ? blueFill : greyFill;
      });
    });

    // Autoajuste de columnas según el contenido.
    worksheet.columns.forEach((column) => {
      let maxLength = column.header ? column.header.toString().length : 10;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const cellLength = cell.value ? cell.value.toString().length : 10;
        if (cellLength > maxLength) maxLength = cellLength;
      });
      column.width = maxLength + 5;
    });

    return workbook.xlsx.writeBuffer();
  }
}
